import { Link } from 'react-router';
import { Sheet } from '../../components/Sheet';
import { Avatar, Spinner } from '../../components/ui';
import { useTerritory } from '../../hooks/queries';
import { timeAgo } from '../../lib/format';
import { useAuth } from '../../stores/auth';
import { MY_COLOR, colorFor } from '../../lib/colors';
import { useT } from '../../i18n';
import type { SponsoredCampaignDto } from '@rw/shared';
import { formatDate } from '../../lib/format';

export function TerritorySheet({
  regionId,
  gridKey,
  sponsor,
  onClose,
}: {
  regionId: string;
  gridKey: string | null;
  /** The running campaign sponsoring this cell, if any. */
  sponsor?: SponsoredCampaignDto;
  onClose: () => void;
}) {
  const me = useAuth((s) => s.user?.id);
  const q = useTerritory(regionId, gridKey);
  const tr = useT();
  const t = q.data;
  const mine = t?.owner?.id === me;
  return (
    <Sheet open={!!gridKey} onClose={onClose} label={tr('Territory')}>
      <div data-testid="territory-sheet">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-ink-400">{tr('Sector')}</div>
            <div className="font-display text-3xl font-bold">{gridKey}</div>
          </div>
          {t ? (
            <span
              className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${
                !t.owner ? 'bg-ink-700 text-ink-300' : mine ? 'bg-volt/15 text-volt' : 'bg-enemy/15 text-enemy'
              }`}
            >
              {tr(!t.owner ? 'Neutral' : mine ? 'Yours' : 'Enemy')}
            </span>
          ) : null}
        </div>

        {sponsor ? <SponsorCard sponsor={sponsor} /> : null}

        {q.isPending ? (
          <div className="grid h-28 place-items-center text-volt">
            <Spinner />
          </div>
        ) : q.isError ? (
          <p className="py-6 text-sm text-ink-400">{tr('Couldn’t load this sector right now.')}</p>
        ) : t ? (
          <>
            {t.owner ? (
              <Link to={`/players/${t.owner.id}`} className="mt-4 flex items-center gap-3 rounded-2xl bg-ink-800 p-3" onClick={onClose}>
                <Avatar name={t.owner.displayName} color={mine ? MY_COLOR : t.owner.color} size="sm" />
                <div className="flex-1">
                  <div className="font-semibold">{mine ? tr('You') : t.owner.displayName}</div>
                  <div className="text-xs text-ink-400">
                    {t.owner.club ? `${t.owner.club.name} · ` : ''}{tr('Held since {time}', { time: t.capturedAt ? timeAgo(t.capturedAt) : '—' })}
                  </div>
                </div>
              </Link>
            ) : (
              <p className="mt-4 rounded-2xl bg-ink-800 p-3 text-sm text-ink-300">
                {tr('Unclaimed. Run 30 m inside this sector with good GPS to capture it.')}
              </p>
            )}
            {!mine && t.owner ? (
              <p className="mt-3 text-sm text-ink-300">{tr('Run through it to take it from {name}. ⚔️', { name: t.owner.displayName })}</p>
            ) : null}
            {t.history.length > 1 ? (
              <div className="mt-4">
                <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-ink-400">{tr('History')}</div>
                <ul className="space-y-1 text-sm">
                  {t.history.map((h) => (
                    <li key={h.runId} className="flex justify-between text-ink-300">
                      <span style={{ color: colorFor(h.user, me) }}>{h.user.id === me ? tr('You') : h.user.displayName}</span>
                      <span className="text-ink-500">{timeAgo(h.capturedAt)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Sheet>
  );
}

/** A sponsored cell: the brand, its bonus and a link to it. */
function SponsorCard({ sponsor }: { sponsor: SponsoredCampaignDto }) {
  const t = useT();
  const { brand } = sponsor;
  return (
    <div className="mt-4 rounded-2xl border p-3" style={{ borderColor: brand.color }} data-testid="sponsor-cell-card">
      <div className="flex items-center gap-3">
        <div className="relative h-12 w-20 shrink-0 overflow-hidden rounded-lg border border-ink-700 bg-ink-850">
          {brand.logoUrl ? (
            <img src={brand.logoUrl} alt={brand.name} className="absolute inset-0 h-full w-full object-contain p-1" />
          ) : (
            <span className="absolute inset-0 grid place-items-center px-1 text-center text-xs font-bold">{brand.name}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-400">{t('Sponsored sector')}</div>
          <div className="truncate font-semibold">{brand.name}</div>
          <div className="text-xs text-ink-400">{t('Until {date}', { date: formatDate(sponsor.endsAt) })}</div>
        </div>
      </div>
      <p className="mt-2 text-sm text-gold" data-testid="sponsor-cell-bonus">
        {t('+{xp} XP the first time you capture it during this campaign.', { xp: sponsor.bonusXp })}
      </p>
      {sponsor.link ? (
        <a
          href={sponsor.link}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="mt-2 block text-sm font-semibold text-volt"
          data-testid="sponsor-cell-link"
        >
          {t('Visit {name} ↗', { name: brand.name })}
        </a>
      ) : null}
    </div>
  );
}
